// components/payments/WithdrawModal.tsx
import React, { useState } from "react";
import {
  X,
  DollarSign,
  CreditCard,
  Building2,
  AlertCircle,
  CheckCircle,
  Loader2,
} from "lucide-react";

interface WithdrawModalProps {
  availableBalance: number;
  onClose: () => void;
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({
  availableBalance,
  onClose,
}) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"bank" | "paypal">("bank");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const minimumWithdrawal = 25;
  const fee = method === "bank" ? 0 : 0.5;
  const numericAmount = parseFloat(amount) || 0;
  const netAmount = Math.max(0, numericAmount - (numericAmount * fee) / 100);

  const handleWithdraw = async () => {
    setError("");

    if (numericAmount < minimumWithdrawal) {
      setError(`Minimum withdrawal amount is $${minimumWithdrawal}`);
      return;
    }

    if (numericAmount > availableBalance) {
      setError("Insufficient balance");
      return;
    }

    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsLoading(false);
    setIsSuccess(true);
  };

  const quickAmounts = [50, 100, 250, 500];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Withdraw Funds</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSuccess ? (
            /* Success State */
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Withdrawal Initiated
              </h3>
              <p className="text-gray-500 mb-6">
                ${numericAmount.toFixed(2)} is being transferred to your{" "}
                {method === "bank" ? "bank account" : "PayPal"}.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Available Balance */}
              <div className="p-4 bg-gray-50 rounded-xl mb-6">
                <p className="text-sm text-gray-500">Available Balance</p>
                <p className="text-2xl font-bold text-gray-900">
                  $
                  {availableBalance.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>

              {/* Amount Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount to Withdraw
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all"
                  />
                </div>

                {/* Quick Amounts */}
                <div className="flex gap-2 mt-3">
                  {quickAmounts.map((quickAmount) => (
                    <button
                      key={quickAmount}
                      onClick={() => setAmount(quickAmount.toString())}
                      disabled={quickAmount > availableBalance}
                      className="flex-1 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:border-[#8B52FF] hover:text-[#8B52FF] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      ${quickAmount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Withdrawal Method */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Withdrawal Method
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setMethod("bank")}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      method === "bank"
                        ? "border-[#8B52FF] bg-[#8B52FF]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Building2
                      className={`w-6 h-6 mx-auto mb-2 ${method === "bank" ? "text-[#8B52FF]" : "text-gray-400"}`}
                    />
                    <p
                      className={`text-sm font-medium ${method === "bank" ? "text-[#8B52FF]" : "text-gray-700"}`}
                    >
                      Bank Transfer
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">No fees</p>
                  </button>
                  <button
                    onClick={() => setMethod("paypal")}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      method === "paypal"
                        ? "border-[#8B52FF] bg-[#8B52FF]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <CreditCard
                      className={`w-6 h-6 mx-auto mb-2 ${method === "paypal" ? "text-[#8B52FF]" : "text-gray-400"}`}
                    />
                    <p
                      className={`text-sm font-medium ${method === "paypal" ? "text-[#8B52FF]" : "text-gray-700"}`}
                    >
                      PayPal
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">0.5% fee</p>
                  </button>
                </div>
              </div>

              {/* Summary */}
              {numericAmount > 0 && (
                <div className="p-4 bg-gray-50 rounded-xl mb-6 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Withdrawal Amount</span>
                    <span className="text-gray-900">
                      ${numericAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Fee ({fee}%)</span>
                    <span className="text-gray-900">
                      -${((numericAmount * fee) / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="flex justify-between font-semibold">
                    <span className="text-gray-700">You'll Receive</span>
                    <span className="text-gray-900">
                      ${netAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleWithdraw}
                disabled={isLoading || !numericAmount}
                className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Withdraw ${numericAmount.toFixed(2)}</>
                )}
              </button>

              <p className="text-xs text-center text-gray-400 mt-4">
                Minimum withdrawal: ${minimumWithdrawal}. Processing time: 1-3
                business days.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawModal;
